/**
 * Interface for aggregated royalties data
 */
export interface IAggregatedRoyaltiesData {
    territory: string;
    vendor: string;
    [month: string]: string | number; // Dynamic month columns with royalty values in USD
}

/**
 * Interface for aggregated MSS data
 */
export interface IAggregatedMssData {
    territory: string;
    vendor: string;
    [month: string]: string | number; // Dynamic month columns with MSS percentage values
}

/**
 * Interface for raw royalties data from CSV
 */
export interface IRoyaltiesRawData {
    id: number;
    service_name: string;
    territory_name: string;
    month: string;
    royalty_vendor_code: string;
    catalog_vendor_code: string;
    plan_name: string;
    rate_card_amount: number;
    rate_card_currency: string;
    settlement_amount: number;
    settlement_currency: string;
    fx_rate: number;
    royalty_model_code: string;
    rate: string;
    is_final: boolean;
    settlement_amount_post_adjustments: number;
    service: string;
    territory: string;
    play_type_name: string;
    program_type_name: string;
    eligible_for_earnings_submission: boolean;
}

/**
 * Interface for raw MSS data from CSV
 */
export interface IMssRawData {
    id: number;
    service: string;
    territory_code: string;
    month: string;
    royalty_vendor_code: string;
    catalog_vendor_code: string;
    plan_name: string;
    total_plays_for_vendor_plan: number;
    total_plays_for_plan: number;
    total_music_plays_for_vendor_plan: number;
    total_music_plays_for_plan: number;
    play_type: string;
    program_type: string;
    eligible_for_earnings_submission: boolean;
    anti_dilution_vendor_share: number;
    standard_vendor_share: number;
    is_artist_centric: number;
}

/**
 * Interface for the joined/aggregated metrics data
 */
export interface IMetricsData {
    vendor_name: string;
    service: string;
    territory: string;
    month: string;
    subscriptions: number;
    vendor_plays: number;
    total_plays: number;
    pro_rata: number; // MSS percentage
    final_royalties_usd: number;
}

/**
 * Interface for chart data configuration
 */
export interface IChartData {
    type: 'bar' | 'line' | 'pie';
    title: string;
    subtitle?: string;
    data: any[];
    xKey?: string;
    yKeys?: string[];
    angleKey?: string;
    labelKey?: string;
    series?: any[];
    groupKey?: string; // For grouped bar charts, specifies the grouping key
    stackGroups?: Record<string, string>; // Maps yKeys to stackGroups
}

/**
 * Format for displaying months (e.g., "202401" -> "Jan 2024")
 */
export const formatMonth = (month: string): string => {
    if (!month || month.length !== 6) return month;

    const year = month.substring(0, 4);
    const monthNum = parseInt(month.substring(4, 6), 10);

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return `${monthNames[monthNum - 1]} ${year}`;
};
